// Bearings, compass points and the estimated time of arrival (R548).
//
// The chart's order lines (card 42) label a course with the bearing and the
// time it takes. Both readings state the same figure: the modern reading
// writes `BRG 214° · ETA 0:41` and the historical one writes the compass
// point in words. This leaf holds the arithmetic; the piece holds the
// sentence.
//
// North is up and the angle grows clockwise, which is what a compass does
// and what the map's y axis (down the screen) makes of `atan2`.

/**
 * The bearing from (x0, y0) to (x1, y1) in degrees, 0 at north (up the
 * screen), growing clockwise, in [0, 360).
 *
 * The two points at the same place answer 0, because a course of no length
 * has no direction and 0 is the figure a reader can ignore.
 */
export function bearingDeg(x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0, dy = y1 - y0;
  // `atan2(0, -0)` is pi, not 0: the negated zero of a course of no length
  // would read due south. Answer the figure a reader can ignore instead.
  if (dx === 0 && dy === 0) return 0;
  const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  const wrapped = (deg + 360) % 360;
  return Number.isFinite(wrapped) ? wrapped : 0;
}

/**
 * The compass point of a bearing, from a table of any length.
 *
 * The table must run clockwise from north, so `points[0]` is N. The 32-point
 * card of the historical reading and the 8-point card of a short label are
 * the same call with a different table: the step is `360 / points.length`.
 */
export function compassPoint(deg: number, points: readonly string[]): string {
  // A bearing that is no number names no point, as `formatBearing` writes
  // dashes for it (R2239).
  if (points.length === 0 || !Number.isFinite(deg)) return '';
  const step = 360 / points.length;
  const wrapped = ((deg % 360) + 360) % 360;
  const k = Math.round(wrapped / step) % points.length;
  return points[k];
}

/** `deg` as the three-figure form a chart writes: 47 becomes `047`. */
export function formatBearing(deg: number): string {
  if (!Number.isFinite(deg)) return '---';
  const whole = ((Math.round(deg) % 360) + 360) % 360;
  return String(whole).padStart(3, '0');
}

/**
 * The seconds a hull needs to run `distPx` at `speedPxPerSec`.
 *
 * A speed of zero or less answers `Infinity`: a hull that does not move
 * never arrives, and a caller that divides by it would write a figure the
 * reader cannot use. `formatEta` turns that into the dashes.
 */
export function etaSec(distPx: number, speedPxPerSec: number): number {
  if (!Number.isFinite(distPx) || distPx < 0) return Number.POSITIVE_INFINITY;
  if (!Number.isFinite(speedPxPerSec) || speedPxPerSec <= 0) return Number.POSITIVE_INFINITY;
  return distPx / speedPxPerSec;
}

/**
 * `sec` as `h:mm`, with the seconds put up to the next whole minute.
 *
 * The chart states an arrival in hours and minutes, never in seconds: a
 * figure that counts down every second is noise on a map that repacks its
 * labels once a minute. 2460 s reads `0:41` and 3900 s reads `1:05`. A
 * value that is not a finite count of seconds reads `--`.
 */
export function formatEta(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '--';
  const minutes = Math.ceil(sec / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}
